import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Server, ShieldCheck, AlertTriangle, Wifi, RefreshCw } from 'lucide-react';

const nodeStatusClasses = (statusValue) => {
  switch ((statusValue || '').toLowerCase()) {
    case 'healthy':
      return 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700';
    case 'warning':
      return 'border-amber-500/30 bg-amber-500/10 text-amber-700';
    case 'critical':
      return 'border-red-500/30 bg-red-500/10 text-red-700';
    default:
      return 'border-muted bg-muted/30 text-muted-foreground';
  }
};

const formatSecondsAgo = (seconds) => {
  if (seconds === null || seconds === undefined) return 'Unknown';
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m ago`;
};

export const NodeHealthCard = ({ nodeHealth, nodeHealthRefreshing, onRefresh }) => (
  <Card id="network" className="mt-8 border-border/70" data-testid="node-health-section">
    <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <CardTitle className="text-lg flex items-center gap-2" data-testid="node-health-title">
          <Server className="h-5 w-5 text-emerald-600" /> Network · Krown Node Health
        </CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          RPC sync, peers, latest block, and indexer lag for Krown Network operations.
        </p>
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={onRefresh}
        disabled={nodeHealthRefreshing}
        data-testid="node-health-refresh-button"
        className="w-full sm:w-auto"
      >
        <RefreshCw className={`h-4 w-4 mr-2 ${nodeHealthRefreshing ? 'animate-spin' : ''}`} />
        Refresh Nodes
      </Button>
    </CardHeader>
    <CardContent>
      {!nodeHealth ? (
        <div className="h-24 rounded-lg bg-muted/40 animate-pulse" data-testid="node-health-loading" />
      ) : (
        <div className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className={`rounded-lg border p-4 ${nodeStatusClasses(nodeHealth.status)}`} data-testid="node-health-overall-status">
              <div className="flex items-center gap-2 text-sm font-medium mb-1">
                <ShieldCheck className="h-4 w-4" /> Overall
              </div>
              <p className="text-2xl font-bold capitalize">{nodeHealth.status}</p>
            </div>
            <div className="rounded-lg border border-border/70 p-4" data-testid="node-health-latest-block">
              <p className="text-sm text-muted-foreground mb-1">Latest Chain Block</p>
              <p className="text-2xl font-mono font-bold">{nodeHealth.indexers?.latest_chain_block?.toLocaleString() || '—'}</p>
            </div>
            <div className="rounded-lg border border-border/70 p-4" data-testid="node-health-indexer-lag">
              <p className="text-sm text-muted-foreground mb-1">Indexer Lag</p>
              <p className={`text-2xl font-mono font-bold ${(nodeHealth.indexers?.lag_blocks || 0) > (nodeHealth.thresholds?.lag_blocks || 50) ? 'text-amber-600' : 'text-emerald-600'}`}>
                {nodeHealth.indexers?.lag_blocks === null || nodeHealth.indexers?.lag_blocks === undefined ? '—' : `${nodeHealth.indexers.lag_blocks.toLocaleString()} blocks`}
              </p>
            </div>
            <div className="rounded-lg border border-border/70 p-4" data-testid="node-health-alert-count">
              <p className="text-sm text-muted-foreground mb-1">Active Alerts</p>
              <p className={`text-2xl font-mono font-bold ${(nodeHealth.alerts || []).length ? 'text-amber-600' : 'text-emerald-600'}`}>{(nodeHealth.alerts || []).length}</p>
            </div>
          </div>

          {(nodeHealth.alerts || []).length > 0 && (
            <div className="space-y-2" data-testid="node-health-alerts">
              {nodeHealth.alerts.map((alert, idx) => (
                <div key={`${alert.message}-${idx}`} className={`flex items-start gap-2 rounded-lg border p-3 text-sm ${alert.severity === 'critical' ? 'border-red-500/30 bg-red-500/10 text-red-700' : 'border-amber-500/30 bg-amber-500/10 text-amber-700'}`} data-testid={`node-health-alert-${idx}`}>
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>{alert.message}</span>
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="node-health-endpoints">
            {(nodeHealth.endpoints || []).map((endpoint, idx) => (
              <div key={`${endpoint.label}-${idx}`} className="rounded-lg border border-border/70 p-4 bg-background/80" data-testid={`node-health-endpoint-${idx}`}>
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <div className={`status-dot ${endpoint.healthy ? (endpoint.syncing ? 'syncing' : 'online') : 'offline'}`} />
                      <p className="font-semibold truncate" data-testid={`node-health-endpoint-label-${idx}`}>{endpoint.label}</p>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Chain ID {endpoint.chain_id_decimal || '—'} · {endpoint.response_time_ms || '—'}ms</p>
                  </div>
                  <Badge variant="outline" className={endpoint.healthy ? 'border-emerald-500/40 text-emerald-700' : 'border-red-500/40 text-red-700'} data-testid={`node-health-endpoint-badge-${idx}`}>
                    {endpoint.healthy ? (endpoint.syncing ? 'Syncing' : 'Online') : 'Offline'}
                  </Badge>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-muted-foreground">Latest Block</p>
                    <p className="font-mono font-semibold" data-testid={`node-health-endpoint-block-${idx}`}>{endpoint.latest_block?.toLocaleString() || '—'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Peers</p>
                    <p className="font-mono font-semibold flex items-center gap-1" data-testid={`node-health-endpoint-peers-${idx}`}><Wifi className="h-3 w-3" /> {endpoint.peer_count ?? '—'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Latest Block Age</p>
                    <p className="font-mono font-semibold" data-testid={`node-health-endpoint-age-${idx}`}>{formatSecondsAgo(endpoint.seconds_since_latest_block)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Sync Remaining</p>
                    <p className="font-mono font-semibold" data-testid={`node-health-endpoint-sync-${idx}`}>{endpoint.sync_progress?.remaining_blocks?.toLocaleString?.() || (endpoint.syncing ? 'Syncing' : '0')}</p>
                  </div>
                </div>
                {endpoint.error && <p className="mt-3 text-xs text-red-600 break-words" data-testid={`node-health-endpoint-error-${idx}`}>{endpoint.error}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </CardContent>
  </Card>
);
